import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useLocalizedStrings } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { DeviceType, SyncSettingsMode } from 'app/model/Device';
import { DeviceList } from 'app/home/main-pages/devices/Devices';
import { LegacyPopup } from 'app/home/general-components/Popup/Popup';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconSettingsGears } from 'images/setting/settings-gears.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';
import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconDeleteRed } from 'images/general/delete-red.svg';

import './SyncSettings.scss'

const nonValidFollowerDeviceTypes = [];
const syncableSets = [
    [DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.plug, DeviceType.siren],
    [DeviceType.dpst],
    [DeviceType.lampX4, DeviceType.relay4],
]; // pair-wise intersection of these sets should be none.

export const SupportedSyncableTypes = [
    DeviceType.lamp, DeviceType.lampX1TwoWay, DeviceType.plug, DeviceType.siren,
    DeviceType.dpst,
    DeviceType.lampX4, DeviceType.relay4
];

export function SyncSettings({ devices, setDeviceFolowers }) {
    const s = useLocalizedStrings();
    const ss = s.deviceSettings.syncSettings;

    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const dismiss = p.dismiss;
    const connected = p.state.connected;

    const [pageDeviceMac, setPageDeviceMac] = useState(p.state.mac);

    const [
        mainDevice, mainDeviceType, mainDeviceMac,
        followerMacs, followerTypes, followerDevices] =
        useMemo(() => {
            let mainDevice;
            let pageDevice = devices.find(({ mac }) => mac === pageDeviceMac);
            if (pageDevice.sync_settings === undefined) {
                mainDevice = pageDevice;
            } else {
                if (pageDevice.sync_settings.mode === SyncSettingsMode.main) {
                    mainDevice = pageDevice;
                } else if (pageDevice.sync_settings.mode === SyncSettingsMode.follower) {
                    mainDevice = devices.find(({ mac }) => mac === pageDevice.sync_settings.main);
                } else { // none
                    mainDevice = pageDevice;
                }
            }

            let _mode = undefined;
            let _followers = undefined;
            if (mainDevice.sync_settings !== undefined) {
                _mode = mainDevice.sync_settings.mode; // dont trust that has value
                _followers = mainDevice.sync_settings.followers; // dont trust that has value
            }
            let followerDevices;
            if ((_mode === SyncSettingsMode.main) && (_followers !== undefined)) {
                followerDevices = devices.filter(({ mac }) => _followers.includes(mac));
            } else {
                followerDevices = [];
            }
            const followerTypes = followerDevices.map(d => d.type);
            const followerMacs = followerDevices.map(d => d.mac);

            return [mainDevice, mainDevice.type, mainDevice.mac, followerMacs, followerTypes, followerDevices];
        }, [pageDeviceMac, devices]);

    const editMainDevice = useCallback(() => {
        hs.push("/devices/detail/sync_settings/devices", {
            editMainDevice: true,
            mainDeviceMac, mainDeviceType, followerMacs, followerTypes, connected, setPageDeviceMac
        })
    }, [hs, mainDeviceMac, mainDeviceType, followerMacs, followerTypes, connected]);

    const editFollowerDevices = useCallback(() => {
        hs.push("/devices/detail/sync_settings/devices", {
            editMainDevice: false,
            mainDeviceMac, mainDeviceType, followerMacs, followerTypes, connected
        })
    }, [hs, mainDeviceMac, mainDeviceType, followerMacs, followerTypes, connected]);

    const [deletePopupIsOpen, setDeletePopupIsOpen] = useState(false);
    const dismissDeletePopup = useCallback(() => {
        setDeletePopupIsOpen(undefined);
    }, []);
    const confirmDeletePopup = useCallback(() => {
        setDeviceFolowers(mainDeviceMac, mainDeviceType, {
            "mode": SyncSettingsMode.none,
        });
        dismiss();
    }, [mainDeviceMac, mainDeviceType, dismiss, setDeviceFolowers])

    return (
        <div className="sync-settings-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="sync-settings-page-content">

                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />

                    <div className="button-border" onClick={() => { setDeletePopupIsOpen(true) }}>
                        <div className="label">{ss.removeSettings}</div>
                        <IconDeleteWhite className="icon" />
                    </div>
                </div>

                <div className="nav-button" onClick={dismiss} />

                <div className="page-header with-margin-bottom">{ss.header}</div>
                <div className="page-header-info">{ss.hint}</div>

                <div className="section-header-group">
                    <div className="label">{ss.main}</div>
                    <div />
                    <div className="border" onClick={editMainDevice}>
                        <div className="label">{ss.edit1}</div>
                        <IconSettingsGears className="icon" />
                    </div>
                </div>

                <DeviceList
                    style={{ overflow: "hidden", marginTop: 12 }}
                    devices={[mainDevice]}
                    bottomPaddingSize={0}
                    showStates={true}
                    dontOpenSettings={true}
                    showSubDevices={true}
                    showDCEvenWhenNotShowingStates={true} />

                <div className="section-header-group" style={{ marginTop: 8 }}>
                    <div className="label">{ss.follower}</div>
                    <div />
                    {followerDevices.length > 0 ?
                        <div className="border" onClick={editFollowerDevices}>
                            <div className="label">{ss.edit}</div>
                            <IconSettingsGears className="icon" />
                        </div> : null}
                </div>

                {followerDevices.length === 0 ?
                    <div className="big-center-button" onClick={editFollowerDevices}>
                        <div className="label">{ss.editButtonExplaned}</div>
                        <IconSettingsGears className="icon" />
                    </div> :
                    <DeviceList
                        style={{ overflow: "hidden", marginTop: 12 }}
                        devices={followerDevices}
                        bottomPaddingSize={0}
                        showStates={true} 
                        dontOpenSettings={true}
                        showSubDevices={true}
                        showDCEvenWhenNotShowingStates={true} />}

                <LegacyPopup show={deletePopupIsOpen} name="remove_permission_popup" baseZIndex={12} dismiss={dismissDeletePopup}>
                    <div className="synch-settings-remove-popup">
                        <div className="header">{ss.areYouSure}</div>
                        <div className="buttons-group">
                            <div className="delete-button" onClick={confirmDeletePopup}>
                                <div className="label">{ss.deleteIt}</div>
                                <IconDeleteRed className="icon" />
                            </div>
                            <div className="dismiss-button" onClick={dismissDeletePopup}>{ss.keepIt}</div>
                        </div>
                    </div>
                </LegacyPopup>
            </div>
        </div>
    )
}

export function SyncSettingsSelectDevice({ devices, setDeviceFolowers }) {
    const s = useLocalizedStrings();
    const ss = s.deviceSettings.syncSettings;

    const p = useContext(CurrentPageContext);
    const dismiss = p.dismiss;

    const connected = p.state.connected;
    const editMainDevice = p.state.editMainDevice === true;
    const mainDeviceMac = p.state.mainDeviceMac;
    const mainDeviceType = p.state.mainDeviceType;
    const followerMacs = p.state.followerMacs;
    const followerTypes = p.state.followerTypes;
    const setPageDeviceMac = p.state.setPageDeviceMac;

    const [selectedDevices, setSelectedDevices] = useState(editMainDevice ? [] : followerMacs);

    const devicesForList = useMemo(() => {
        let refrenceSyncableSet = undefined;
        if (editMainDevice) {
            if (followerTypes.length === 0) {
                refrenceSyncableSet = SupportedSyncableTypes;
            } else {
                for (let j = 0; j < syncableSets.length; j++) {
                    const set = syncableSets[j];
                    if (set.includes(followerTypes[0])) {
                        refrenceSyncableSet = set;
                        break;
                    }
                }
            }
        } else {
            for (let j = 0; j < syncableSets.length; j++) {
                const set = syncableSets[j];
                if (set.includes(mainDeviceType)) {
                    refrenceSyncableSet = set;
                    break;
                }
            }
        }

        let _devicesForList = [];
        for (let i = 0; i < devices.length; i++) {
            const device = devices[i];
            if (refrenceSyncableSet.includes(device.type)) {
                if (editMainDevice) {
                    _devicesForList.push(device);
                } else {
                    if (!nonValidFollowerDeviceTypes.includes(device.type)) {
                        if (device.mac !== mainDeviceMac) {
                            _devicesForList.push(device);
                        }
                    }
                }
            }
        }
        return _devicesForList;
    }, [editMainDevice, mainDeviceMac, mainDeviceType, followerTypes, devices]);

    const selectionCallback = useCallback((device) => {
        if (editMainDevice) {
            let newFollowerMacs = [];
            for (let i = 0; i < followerMacs.length; i++) {
                const fmac = followerMacs[i];
                if (fmac !== device.mac) {
                    newFollowerMacs.push(fmac)
                }
            }
            if (newFollowerMacs.length > 0) {
                setDeviceFolowers(device.mac, device.type, {
                    "mode": SyncSettingsMode.main,
                    "followers": newFollowerMacs,
                });
            } else {
                setDeviceFolowers(device.mac, device.type, {
                    "mode": SyncSettingsMode.none,
                });
            }
            setPageDeviceMac(device.mac);
            dismiss();
        } else {
            const newSelectedDevices = [...selectedDevices];
            const index = selectedDevices.findIndex((m) => m === device.mac);
            if (index >= 0) {
                newSelectedDevices.splice(index, 1);
            } else {
                newSelectedDevices.push(device.mac);
            }
            setSelectedDevices(newSelectedDevices);
        }
    }, [editMainDevice, followerMacs, setDeviceFolowers, selectedDevices, setSelectedDevices, setPageDeviceMac, dismiss]);

    const saveNewFollowers = useCallback(() => {
        if (selectedDevices.length > 0) {
            setDeviceFolowers(mainDeviceMac, mainDeviceType, {
                "mode": SyncSettingsMode.main,
                "followers": selectedDevices,
            });
        } else {
            setDeviceFolowers(mainDeviceMac, mainDeviceType, {
                "mode": SyncSettingsMode.none,
            });
        }
        dismiss();
    }, [mainDeviceMac, mainDeviceType, selectedDevices, setDeviceFolowers, dismiss])

    return (
        <div className="sync-settings-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="sync-settings-page-content">
                <CustomIconBack className="nav-icon" /> <div className="nav-button" onClick={dismiss} />

                <div className="page-header with-margin-bottom">{editMainDevice ? ss.edit1 : ss.edit}</div>
                {editMainDevice ? null : <div className="page-header-info">{ss.editInfo}</div>}

                <DeviceList
                    style={{ marginTop: 16 }}
                    devices={devicesForList}
                    selectedDevices={selectedDevices}
                    selectionCallback={selectionCallback}
                    showStates={false}
                    dontOpenSettings={true}
                    showSubDevices={false}
                    key={"sync_settings_list_edit_devices"} />

                {editMainDevice ? null :
                    <div className="full-blue-button next-button" onClick={saveNewFollowers}>
                        <div>{s.save}</div> <IconTick className="icon" />
                    </div>}
            </div>
        </div>
    );
} 
